import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatQty } from '../lib/quantity';
import { extractionsApi, type ExtractionCurrentYearStats } from '../services/extractions';

type ProductionCard = {
  title: string;
  description: string;
  path?: string;
  enabled: boolean;
  cta?: string;
  meta?: string;
  icon: ReactNode;
  secondaryLinks?: Array<{ label: string; path: string; enabled: boolean }>;
};

function CardIcon({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
      {children}
    </div>
  );
}

function DropletIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3.5c-3.7 4.2-5.5 7-5.5 9.3a5.5 5.5 0 1 0 11 0c0-2.3-1.8-5.1-5.5-9.3Z" />
    </svg>
  );
}

function SlidersIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="9" cy="6" r="2" fill="white" />
      <circle cx="15" cy="12" r="2" fill="white" />
      <circle cx="11" cy="18" r="2" fill="white" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3 7 4v10l-7 4-7-4V7l7-4Z" />
      <path d="m5 7 7 4 7-4" />
      <path d="M12 11v10" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10.5 12.5 3H5v7.5L12.5 18 20 10.5Z" />
      <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="6" height="6" rx="1.2" />
      <rect x="14" y="4" width="6" height="6" rx="1.2" />
      <rect x="4" y="14" width="6" height="6" rx="1.2" />
      <rect x="14" y="14" width="6" height="6" rx="1.2" />
    </svg>
  );
}

function ThermometerIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 14.7V5a2 2 0 1 0-4 0v9.7a4 4 0 1 0 4 0Z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

export function ProductionPage() {
  const { canRead } = useAuth();
  const { error: toastError } = useToast();
  const [stats, setStats] = useState<ExtractionCurrentYearStats | null>(null);

  useEffect(() => {
    extractionsApi.currentYearStats()
      .then(setStats)
      .catch(() => {
        setStats(null);
        toastError('Kunde inte ladda produktionsstatistik');
      });
  }, [toastError]);

  const cards: ProductionCard[] = [
    {
      title: 'Slungning',
      description: 'Skapa slungningstillfällen och registrera kärl med vikt, vattenhalt och ursprung.',
      path: '/extractions',
      enabled: canRead('extractions'),
      meta: stats ? `${formatQty(stats.extractedKg, 'kg')} slungat under ${stats.year}` : 'Ingen statistik ännu',
      cta: 'Till slungning',
      icon: <DropletIcon />,
    },
    {
      title: 'Blandning',
      description: 'Blanda kärl till ny batch och följ spårbarheten från ingående kärl till resultat.',
      path: '/blending',
      enabled: canRead('bottling'),
      meta: 'Honung och pollen med full spårkedja',
      cta: 'Till blandning',
      icon: <SlidersIcon />,
    },
    {
      title: 'Tappning',
      description: 'Välj batch, välj emballage och skapa tappning med rätt antal och rätt spårning.',
      path: '/bottling',
      enabled: canRead('bottling'),
      meta: 'Burkar och färdigvaror med batchkoppling',
      cta: 'Till tappning',
      icon: <BoxIcon />,
    },
    {
      title: 'Smaksättning',
      description: 'Skapa recept, välj källhink och kör smaksättning med yield, spill och full spårbarhet.',
      path: '/flavor',
      enabled: canRead('bottling'),
      meta: 'Recept och lagerföring från honung till ny H-hink',
      cta: 'Till smaksättning',
      icon: <TagIcon />,
    },
    {
      title: 'Pollen',
      description: 'Registrera skörd per bigård, uppdatera batchstatus och tappa pollen till kundemballage med spårbar batchkedja.',
      path: '/pollen',
      enabled: canRead('bottling'),
      meta: 'Skörd, status och tappning av pollen',
      cta: 'Till pollen',
      icon: <GridIcon />,
    },
    {
      title: 'Tapprum',
      description: 'Visa tapptank, hålltankar, krämare och värmeskåp med livevärden från Home Assistant och batcher från ApisAI.',
      path: '/honeyroom',
      enabled: canRead('bottling'),
      meta: 'Dashboard för tankar, batcher och flyttar',
      cta: 'Till tapprum',
      icon: <ThermometerIcon />,
    },
    {
      title: 'Bivaxhantering',
      description: 'Smältning, rening, batchning och produktionsserier för bivax byggs i senare steg.',
      enabled: false,
      meta: 'Planerad modul',
      icon: <GridIcon />,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Produktion</h1>
        <p className="max-w-3xl text-base leading-7 text-slate-600">
          Slungning, blandning, tappning och smaksättning. Bygg spårbar kedja från kärl till färdig vara.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <div
            key={card.title}
            className="group flex min-h-[220px] flex-col rounded-[28px] border border-slate-300 bg-white px-5 py-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(15,23,42,0.08)]"
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div className="text-[18px] font-semibold tracking-[-0.02em] text-slate-950 md:text-[20px]">{card.title}</div>
              <CardIcon>{card.icon}</CardIcon>
            </div>

            <p className="text-[14px] leading-8 text-slate-600 md:text-[15px]">{card.description}</p>

            {card.meta ? <div className="mt-5 text-[13px] text-slate-500">{card.meta}</div> : null}

            <div className="mt-auto pt-6">
              {card.enabled && card.path && card.cta ? (
                <Link to={card.path} className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:text-primary-hover">
                  <span>{card.cta}</span>
                  <ArrowIcon />
                </Link>
              ) : null}

              {card.secondaryLinks && card.secondaryLinks.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-3">
                  {card.secondaryLinks.map((link) =>
                    link.enabled ? (
                      <Link
                        key={link.label}
                        to={link.path}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                      >
                        <span>{link.label}</span>
                        <ArrowIcon />
                      </Link>
                    ) : (
                      <span
                        key={link.label}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm"
                      >
                        <span>{link.label}</span>
                        <ArrowIcon />
                      </span>
                    ),
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
